import { Controller } from '@hotwired/stimulus'
import { EditorState } from '@codemirror/state'
import { EditorView, keymap } from '@codemirror/view'
import { indentWithTab } from '@codemirror/commands'
import { search } from '@codemirror/search'
import { linter, lintGutter } from '@codemirror/lint'
import { yaml } from '@codemirror/lang-yaml'
import { basicSetup } from 'codemirror'
import { parseAllDocuments } from 'yaml'

// Structural diagnostics — misaligned indentation, duplicated keys, unclosed
// quotes. This parser keeps going past an error, so every problem gets its own
// marker rather than only the first. The message's code frame is dropped: the
// marker already points at the spot.
export const yamlDiagnostics = state => {
  const clamp = position => Math.min(position ?? 0, state.doc.length)
  const diagnostic = severity => issue => {
    const from = clamp(issue.pos?.[0])
    return { from, to: Math.max(from, clamp(issue.pos?.[1])), severity, message: issue.message.split('\n')[0] }
  }

  return parseAllDocuments(state.doc.toString()).flatMap(document => [
    ...document.errors.map(diagnostic('error')),
    ...document.warnings.map(diagnostic('warning'))
  ])
}

// Keeps the editor self-contained: it scrolls internally instead of ballooning
// to the full document height (which made Cmd-F navigation scroll the whole page).
// Heights are overridable per-instance via data-yaml-min-height / -max-height so the
// large definition editors can fill the pane while small snippet editors stay compact.
export const buildEditorTheme = ({ minHeight, maxHeight }) =>
  EditorView.theme({
    '&': {
      maxHeight,
      minHeight,
      border: '1px solid #ced4da',
      borderRadius: '0.375rem',
      backgroundColor: '#fff',
      fontSize: '13px'
    },
    '&.cm-focused': {
      outline: 'none',
      borderColor: '#86b7fe',
      boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.15)'
    },
    '.cm-scroller': {
      overflow: 'auto',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      lineHeight: '1.5'
    },
    '.cm-panels': {
      backgroundColor: '#f8f9fa',
      color: '#212529',
      borderTop: '1px solid #dee2e6'
    },
    '.cm-panel.cm-search': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '6px',
      padding: '8px'
    },
    '.cm-panel.cm-search label': { display: 'inline-flex', alignItems: 'center', gap: '3px', margin: '0' },
    '.cm-textfield': { border: '1px solid #ced4da', borderRadius: '0.25rem', padding: '3px 6px' },
    '.cm-button': {
      border: '1px solid #ced4da',
      borderRadius: '0.25rem',
      backgroundImage: 'none',
      backgroundColor: '#fff',
      padding: '3px 10px'
    }
  })

export default class extends Controller {
  connect() {
    const textarea = this.element

    // Turbo Drive can restore a cached snapshot that still contains a previously
    // rendered editor; drop any stale instance before mounting a fresh one.
    const stale = textarea.previousElementSibling
    if (stale && stale.classList.contains('cm-editor')) stale.remove()

    const editorTheme = buildEditorTheme({
      minHeight: textarea.dataset.yamlMinHeight || '180px',
      maxHeight: textarea.dataset.yamlMaxHeight || '70vh'
    })

    const extensions = [
      basicSetup,
      yaml(),
      keymap.of([indentWithTab]),
      search({ top: true }),
      lintGutter(),
      linter(view => yamlDiagnostics(view.state)),
      EditorView.lineWrapping,
      editorTheme,
      ...(textarea.readOnly ? [EditorState.readOnly.of(true), EditorView.editable.of(false)] : [])
    ]

    this.view = new EditorView({
      state: EditorState.create({ extensions, doc: textarea.value })
    })
    textarea.parentNode.insertBefore(this.view.dom, textarea)
    textarea.style.display = 'none'

    this.syncToTextarea = () => {
      textarea.value = this.view.state.doc.toString()
    }
    this.applyExternalChange = e => {
      this.view.dispatch({ changes: { from: 0, to: this.view.state.doc.length, insert: e.detail } })
    }

    if (textarea.form) {
      textarea.form.addEventListener('submit', this.syncToTextarea)
      textarea.form.addEventListener('yaml:change', this.applyExternalChange)
    }
  }

  disconnect() {
    if (this.element.form) {
      this.element.form.removeEventListener('submit', this.syncToTextarea)
      this.element.form.removeEventListener('yaml:change', this.applyExternalChange)
    }
    this.view?.dom.remove()
    this.view?.destroy()
    this.element.style.display = ''
  }
}
